import React, { useRef } from 'react';
import { useDialogFocus } from '../../hook/useFormKeyboard';
import { IconButton, MenuItem } from './controls';
import { Icon } from './icons';
import { DIVIDER, INK, MUTED, RED, TEXT } from './tokens';

interface OverlayProps {
  open: boolean;
  phone: boolean;
  title: string;
  titleId: string;
  closeLabel: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}

/** A centred dialog on desktop, a bottom sheet on phone; Escape, the close button and the backdrop close it. */
export const Overlay: React.FC<OverlayProps> = ({ open, phone, title, titleId, closeLabel, onClose, children, footer, width = 640 }) => {
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(open, ref, onClose);
  if (!open) return null;
  return (
    <div className="pu-scope" style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: phone ? 'flex-end' : 'center', justifyContent: 'center', padding: phone ? 0 : '16px' }}>
      <div aria-hidden="true" onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(5,41,42,0.40)' }} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={phone ? 'pu-sheet' : 'pu-dialog'}
        style={
          phone
            ? { position: 'relative', width: '100%', maxHeight: '88%', display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: '20px 20px 0 0', boxShadow: '0 -12px 40px rgba(5,41,42,0.12)' }
            : { position: 'relative', width: `min(${width}px, 100%)`, maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: '16px', boxShadow: '0 24px 64px rgba(5,41,42,0.18)' }
        }
      >
        {phone && (
          <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px' }}>
            <span style={{ width: '40px', height: '4px', borderRadius: '999px', background: '#DBE4E4' }} />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: phone ? '4px 6px 12px 16px' : '20px 16px 16px 24px' }}>
          <h2 id={titleId} style={{ flex: '1 1 auto', minWidth: 0, fontSize: '18px', lineHeight: '26px', fontWeight: 600, color: INK }}>
            {title}
          </h2>
          <IconButton icon="close" label={closeLabel} phone={phone} onClick={onClose} />
        </div>
        <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: phone ? '0 16px 16px' : '0 24px 16px' }}>{children}</div>
        {footer && (
          <div style={{ padding: phone ? '12px 16px 16px' : '16px 24px', borderTop: `1px solid ${DIVIDER}`, background: '#FFFFFF', borderRadius: phone ? 0 : '0 0 16px 16px' }}>{footer}</div>
        )}
      </div>
    </div>
  );
};

/** The phone "More" sheet: one 52px row per action. */
export const ActionSheet: React.FC<{ open: boolean; title: string; closeLabel: string; items: MenuItem[]; onClose: () => void }> = ({ open, title, closeLabel, items, onClose }) => (
  <Overlay open={open} phone title={title} titleId="pu-actions-title" closeLabel={closeLabel} onClose={onClose}>
    <div style={{ margin: '0 -16px' }}>
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className="pu-menu-item"
          disabled={item.disabled}
          onClick={() => {
            onClose();
            item.onSelect();
          }}
          style={{ width: '100%', minHeight: '52px', display: 'flex', alignItems: 'center', gap: '14px', padding: '8px 20px', border: 0, background: 'transparent', color: item.danger ? RED : TEXT, fontSize: '15px', lineHeight: '22px', textAlign: 'left', cursor: 'pointer' }}
        >
          {item.icon && <Icon name={item.icon} size={20} color={item.danger ? RED : MUTED} />}
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span>{item.label}</span>
            {item.desc && <span style={{ fontSize: '13px', lineHeight: '18px', color: MUTED }}>{item.desc}</span>}
          </span>
        </button>
      ))}
    </div>
  </Overlay>
);

/** Confirm before a step that cannot be undone. */
export const ConfirmDialog: React.FC<{
  open: boolean;
  phone: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  closeLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, phone, title, body, confirmLabel, cancelLabel, closeLabel, danger, busy, onConfirm, onCancel }) => (
  <Overlay
    open={open}
    phone={phone}
    title={title}
    titleId="pu-confirm-title"
    closeLabel={closeLabel}
    onClose={onCancel}
    width={480}
    footer={
      <div style={{ display: 'flex', flexDirection: phone ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: phone ? '8px' : '12px' }}>
        <button
          type="button"
          className="pu-btn-secondary"
          onClick={onCancel}
          style={{ height: phone ? '48px' : '36px', padding: '0 18px', border: '1px solid #C9D7D8', borderRadius: '999px', background: '#FFFFFF', color: TEXT, fontSize: phone ? '15px' : '14px', fontWeight: 500, cursor: 'pointer' }}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={danger ? 'pu-btn-danger-solid' : 'pu-btn-primary'}
          disabled={busy}
          onClick={onConfirm}
          style={{ height: phone ? '48px' : '36px', padding: '0 18px', border: `1px solid ${danger ? RED : '#0F7679'}`, borderRadius: '999px', background: danger ? RED : '#0F7679', color: '#FFFFFF', fontSize: phone ? '15px' : '14px', fontWeight: 500, cursor: 'pointer' }}
        >
          {confirmLabel}
        </button>
      </div>
    }
  >
    <div style={{ fontSize: '14px', lineHeight: '20px', color: MUTED }}>{body}</div>
  </Overlay>
);
